import { useEffect, useState, useMemo } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router";
import classNames from 'classnames';

import { TemplateClassNames } from "../../../../types/template-layouts";
import { BlProfile, BrandliveEvent, LanguagesAbbr } from "../../../../types/working-model";
import { setStorageItem } from "../../../../utils/local-storage";
import { EIcon } from "../../../general-ui/icon/icon";
import { ALL_LANGS, ILang } from "../../../general-ui/language-dropdown/language-dropdown";
import './profile-details-dropdown.scss';

interface IProfileDetailsProps {
	channel: number;
	profile: BlProfile | null;
	template: TemplateClassNames;
	eventBundle: BrandliveEvent;
}

const ProfileDetails: React.FC<IProfileDetailsProps> = ({ profile, channel, eventBundle }) => {
	const { language: pathLanguage, ...rest }: any = useParams();
	const routeMatch = useRouteMatch<any>();
	const history = useHistory();

	const [profileDetailsOpen, setProfileDetailsOpen] = useState(false);
	const [showLanguages, setShowLanguages] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState(pathLanguage ?? 'en');
	const [showSecondLevel, setShowSecondLevel] = useState(false);

	const languages = useMemo(() => {
		if (!rest?.sessionUuid) {
			return Array.from(new Set(eventBundle?.homepage?.languages as LanguagesAbbr[]));
		}
		const session = eventBundle?.sessions?.find(session => session.uuid === rest.sessionUuid);
		if (!session) return [];
		return Array
			.from(new Set(eventBundle?.homepage?.languages as LanguagesAbbr[]))
			.filter(lang => session.languages.includes(lang));
	}, [rest, eventBundle]);

	// on escape, close dropdown
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.code === 'Escape' || event.key === 'Escape') {
				setProfileDetailsOpen(false);
			}
		};
		if (profileDetailsOpen) {
			window.addEventListener("keydown", handleKeyDown);
		}
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [profileDetailsOpen]);


	useEffect(() => {
		const handleClick = (e: any) => {
			const path = e.path || e.composedPath();
			for (const item of path) {
				if (item.id === 'profile-details-dropdown-container') {
					return;
				}
			}

			setProfileDetailsOpen(false);
		};

		if (profileDetailsOpen) {
			window.addEventListener("click", handleClick);
		}

		return () => {
			window.removeEventListener("click", handleClick);
		};
	}, [profileDetailsOpen]);

	const handleLanguageChange = (langAbbr: LanguagesAbbr) => () => {
		if (!eventBundle?.event) return;
		if (!routeMatch?.params?.language) return;
		setSelectedLanguage(langAbbr);
		// language is set in storage so we can remember the users chosen language so if they enter
		// a session in another language and then go back to the home page, we can reset the language to their chosen language
		setStorageItem(`event.${eventBundle.event}.language`, langAbbr);
		if (langAbbr && langAbbr !== routeMatch?.params?.language) {
			// replace the language in the url with langAbbr
			const updatedParams = { ...routeMatch.params };
			updatedParams.language = langAbbr;
			let updatedUrl = routeMatch.path;
			for (const key in updatedParams) {
				updatedUrl = updatedUrl.replace(`:${key}`, updatedParams[key]);
			}
			history.replace(updatedUrl);
		}
	};

	const allLanguages: ILang[] = languages
		? (languages
			.map((l) => ALL_LANGS.find((f) => f.code === l))
			.filter((f) => f) as any)
		: ALL_LANGS;

	const handleToggleLanguage = () => {
		setShowSecondLevel(prev => !prev);
		setShowLanguages(prev => !prev);
	};

	return (
		<div className="profile-details-dropdown-container" id="profile-details-dropdown-container">
			<button
				className="profile-details-dropdown-trigger no-style"
				aria-label={
					profileDetailsOpen
						? 'Close profile pop-up'
						: 'Open profile pop-up'
				}
				onClick={() => setProfileDetailsOpen((prev) => !prev)}
			>
				{profile?.profile?.[channel]?.avatar ? (
					<img
						src={profile?.profile?.[channel]?.avatar}
						alt="Profile avatar"
						width="48"
						height="48"
					/>
				) : (
					<div className="profile-details-dropdown-placeholder" />
				)}
				<span>
					<EIcon name="keyboard-arrow-right" />
				</span>
			</button>

			{profileDetailsOpen ? (
				<div className="profile-details-dropdown-menu">

					{/* place all first level nav items in this ternary: */}
					{showSecondLevel ? null : (
						<div className="profile-first-level-actions">
							<button className="no-style nowrap space-between" onClick={handleToggleLanguage}>
								{
									allLanguages.find(lang => lang.code === selectedLanguage)?.label
									|| allLanguages.find(lang => lang.code === pathLanguage)?.label
								} <span style={{ textAlign: 'right' }}><EIcon name="keyboard-arrow-right" /></span>
							</button>
						</div>
					)}

					{showLanguages ? (
						<div className="profile-nav-language-container">
							<button className="no-style btn-left-align profile-second-level-action" onClick={handleToggleLanguage}>
								<span><EIcon name="keyboard-arrow-left" /></span> Back
							</button>
							{allLanguages.map((lang) => (
								<button
									className={classNames('no-style sharp profile-nav-language-item btn-left-align', {
										activeLanguage: selectedLanguage === lang.code,
									})}
									onClick={handleLanguageChange(lang.code)}
									key={lang.code}
								>
									{lang.label}
								</button>
							))}
						</div>
					) : null}

				</div>
			) : null}
		</div>
	);
};

export default ProfileDetails;
